import React from "react";
import { Box } from "@mui/material";
import { dt } from "../../../../shared/themes/redesignTokens";
import OemStar from "../OemStar";
import { LiveDotVisual, PopupDocType } from "../../utils/midasReadiness";

// =====================================================================
//  readinessPopupParts (DR-3) — presentational pieces for the shared
//  MIDAS Data Readiness popup. Exact colours/spacing pulled from Paper
//  artboards v2-2b / v2-2c / v2-2d via get_jsx (inline-styles). House
//  fonts: dt.font (Sora) for UI text, dt.fontMono (JetBrains Mono) for
//  doc chips + language lanes.
// =====================================================================

// --- Exact tokens from the Paper popup artboards ---
export const rp = {
  // Header
  headerBorder: "#EDEFF2",
  eyebrow: "#8A9098",
  title: "#1A1A2E",
  // Header pill cluster
  pillBg: "#F4F6F9",
  midasDot: "#3498DB",
  midasText: "#2E86DE",
  liveGreenDot: "#27AE60",
  liveGreenText: "#27AE60",
  hollowBorder: "#B9BEC6",
  hollowText: "#9AA0A8",
  oemDot: "#C9962E",
  oemText: "#A87F1E",
  grayDot: "#DADEE3",
  grayText: "#C4C8CE",
  // OEM dark-gold panel
  oemPanelBorder: "#C9962E",
  oemPanelGold: "#E3B34C",
  oemPanelRoundelBg: "#E3B34C1F",
  oemPanelRoundelBorder: "#E3B34C66",
  oemPanelTitle: "#FFFFFF",
  oemPanelBody: "#8F8FA8",
  oemCtaText: "#15151F",
  // Doc list
  rowBorder: "#F3F4F6",
  chipLitBg: "#3498DB14",
  chipLitText: "#2980B9",
  chipDashBorder: "#D8DCE1",
  chipDashText: "#B9BEC6",
  langText: "#6B7280",
  langMuted: "#C4C9CF",
  provText: "#9AA0A8",
  provMuted: "#C4C9CF",
  disclaimer: "#9AA0A8",
  // Section badges
  badgeReadyBg: "#27AE601A",
  badgeReadyText: "#27AE60",
  badgeMutedBg: "#F4F6F9",
  badgeMutedBorder: "#E5E7EB",
  badgeMutedText: "#6B7280",
  // Live block
  sectionHeading: "#1A1A2E",
  liveBody: "#6B7280",
  // Outline CTA (Request MachineID integration)
  outlineCtaBorder: "#3498DB",
  outlineCtaText: "#2980B9",
  // Muted OEM package section
  oemMutedHeading: "#B9BEC6",
  oemMutedBody: "#C4C9CF",
  // Footer
  footerBg: "#FAFBFC",
  footerBorder: "#EDEFF2",
  footerText: "#6B7280",
};

// ---------------------------------------------------------------------
//  Header dot (pill cluster) — renders MIDAS / LIVE / OEM signals
// ---------------------------------------------------------------------

type HeaderDotVariant = "lit-blue" | "lit-green" | "lit-gold" | "hollow" | "gray";

const dotStyle = (variant: HeaderDotVariant): object => {
  switch (variant) {
    case "lit-blue":
      return { backgroundColor: rp.midasDot };
    case "lit-green":
      return { backgroundColor: rp.liveGreenDot };
    case "lit-gold":
      return { backgroundColor: rp.oemDot };
    case "hollow":
      return { backgroundColor: "transparent", border: `1.3px solid ${rp.hollowBorder}` };
    default:
      return { backgroundColor: rp.grayDot };
  }
};

const dotTextColor = (variant: HeaderDotVariant): string => {
  switch (variant) {
    case "lit-blue":
      return rp.midasText;
    case "lit-green":
      return rp.liveGreenText;
    case "lit-gold":
      return rp.oemText;
    case "hollow":
      return rp.hollowText;
    default:
      return rp.grayText;
  }
};

const HeaderDot: React.FC<{ label: string; variant: HeaderDotVariant }> = ({ label, variant }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
    <Box sx={{ width: "6px", height: "6px", borderRadius: "50%", flexShrink: 0, ...dotStyle(variant) }} />
    <Box
      component="span"
      sx={{
        fontSize: "10px",
        lineHeight: "12px",
        fontWeight: 600,
        fontFamily: dt.font,
        letterSpacing: "0.04em",
        color: dotTextColor(variant),
      }}
    >
      {label}
    </Box>
  </Box>
);

export const ReadinessHeaderPill: React.FC<{
  midasLit: boolean;
  liveVisual: LiveDotVisual;
  oemLit: boolean;
}> = ({ midasLit, liveVisual, oemLit }) => {
  const liveVariant: HeaderDotVariant =
    liveVisual === "lit" ? "lit-green" : liveVisual === "hollow" ? "hollow" : "gray";
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "9px",
        backgroundColor: rp.pillBg,
        borderRadius: "14px",
        padding: "6px 12px",
        flexShrink: 0,
      }}
    >
      <HeaderDot label="MIDAS" variant={midasLit ? "lit-blue" : "gray"} />
      <HeaderDot label="LIVE" variant={liveVariant} />
      <HeaderDot label="OEM" variant={oemLit ? "lit-gold" : "gray"} />
    </Box>
  );
};

// ---------------------------------------------------------------------
//  Section badge (READY / POSSIBLE / CONNECTED / NO DOCS YET / …)
// ---------------------------------------------------------------------

export const SectionBadge: React.FC<{ label: string; tone: "ready" | "muted" }> = ({ label, tone }) => (
  <Box
    sx={{
      backgroundColor: tone === "ready" ? rp.badgeReadyBg : rp.badgeMutedBg,
      border: tone === "muted" ? `1px solid ${rp.badgeMutedBorder}` : "none",
      borderRadius: "6px",
      padding: "2px 7px",
    }}
  >
    <Box
      component="span"
      sx={{
        fontSize: "10px",
        fontWeight: 700,
        fontFamily: dt.font,
        letterSpacing: "0.08em",
        lineHeight: "12px",
        color: tone === "ready" ? rp.badgeReadyText : rp.badgeMutedText,
      }}
    >
      {label}
    </Box>
  </Box>
);

// ---------------------------------------------------------------------
//  OEM dark-gold support panel (OEM state only)
// ---------------------------------------------------------------------

export const OemSupportPanel: React.FC<{
  eyebrow: string;
  title: string;
  body: string;
  ctaLabel: string;
  onCta: () => void;
}> = ({ eyebrow, title, body, ctaLabel, onCta }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "16px",
      background:
        "linear-gradient(110deg, #1A1B26 0%, #1E2233 70%, #1A1E2C 100%)",
      border: `1px solid ${rp.oemPanelBorder}`,
      borderRadius: "14px",
      m: "20px 24px 0 24px",
      padding: "18px 20px",
    }}
  >
    <Box
      sx={{
        width: "44px",
        height: "44px",
        flexShrink: 0,
        borderRadius: "50%",
        backgroundColor: rp.oemPanelRoundelBg,
        border: `1px solid ${rp.oemPanelRoundelBorder}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <OemStar size={20} fill={rp.oemPanelGold} />
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, gap: "4px", minWidth: 0 }}>
      <Box
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          fontFamily: dt.font,
          letterSpacing: "0.14em",
          lineHeight: "14px",
          color: rp.oemPanelGold,
        }}
      >
        {eyebrow}
      </Box>
      <Box sx={{ fontSize: "14px", fontWeight: 700, fontFamily: dt.font, lineHeight: "18px", color: rp.oemPanelTitle }}>
        {title}
      </Box>
      <Box sx={{ fontSize: "12px", fontFamily: dt.font, lineHeight: "17px", color: rp.oemPanelBody }}>
        {body}
      </Box>
    </Box>
    <Box
      component="button"
      type="button"
      onClick={onCta}
      sx={{
        flexShrink: 0,
        border: "none",
        display: "flex",
        alignItems: "center",
        background: "linear-gradient(135deg, #F2C14E 0%, #D9A227 100%)",
        borderRadius: "24px",
        boxShadow: "0 2px 10px #C9962E59",
        padding: "10px 20px",
        cursor: "pointer",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": { transform: "translateY(-1px)", boxShadow: "0 4px 14px #C9962E80" },
        "&:focus-visible": { outline: `2px solid ${rp.oemPanelGold}`, outlineOffset: "2px" },
      }}
    >
      <Box
        component="span"
        sx={{ fontSize: "13px", fontWeight: 700, fontFamily: dt.font, lineHeight: "16px", color: rp.oemCtaText, whiteSpace: "nowrap" }}
      >
        {ctaLabel}
      </Box>
    </Box>
  </Box>
);

// ---------------------------------------------------------------------
//  Doc-type row (chip / languages / provenance) in fixed lanes
// ---------------------------------------------------------------------

export const DocTypeRow: React.FC<{
  chipLabel: string;
  lit: boolean;
  languages: string; // already formatted, or "—"
  provenance: string;
  isFirst: boolean;
}> = ({ chipLabel, lit, languages, provenance, isFirst }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "9px 0",
      borderTop: isFirst ? "none" : `1px solid ${rp.rowBorder}`,
    }}
  >
    {/* Chip lane. Was a hard 104px; DR-6's "OEM node docs" chip (and its longer
        sk/cs translations, e.g. "OEM dokumentácia uzla") is wider than that and
        would have overlapped the languages column. `minWidth` keeps the lane at
        exactly 104px for every existing chip and only grows for the long one. */}
    <Box sx={{ flexShrink: 0, minWidth: "104px" }}>
      <Box
        sx={{
          display: "inline-block",
          borderRadius: "6px",
          padding: "3px 9px",
          ...(lit
            ? { backgroundColor: rp.chipLitBg }
            : { border: `1px dashed ${rp.chipDashBorder}` }),
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: "11px",
            fontWeight: lit ? 600 : 400,
            fontFamily: dt.fontMono,
            lineHeight: "14px",
            color: lit ? rp.chipLitText : rp.chipDashText,
            whiteSpace: "nowrap",
          }}
        >
          {chipLabel}
          {lit ? " ✓" : ""}
        </Box>
      </Box>
    </Box>
    <Box
      sx={{
        flexShrink: 0,
        width: "72px",
        fontSize: "11px",
        fontFamily: dt.fontMono,
        lineHeight: "14px",
        color: lit ? rp.langText : rp.langMuted,
      }}
    >
      {languages}
    </Box>
    <Box
      sx={{
        flexGrow: 1,
        fontSize: "11.5px",
        fontFamily: dt.font,
        lineHeight: "15px",
        color: lit ? rp.provText : rp.provMuted,
        minWidth: 0,
      }}
    >
      {provenance}
    </Box>
  </Box>
);

// ---------------------------------------------------------------------
//  Outline CTA — "Request MachineID integration"
// ---------------------------------------------------------------------

export const OutlineCta: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      background: "none",
      border: `1.5px solid ${rp.outlineCtaBorder}`,
      borderRadius: "24px",
      padding: "9px 20px",
      cursor: "pointer",
      transition: "background-color 0.15s ease",
      "&:hover": { backgroundColor: "#3498DB0F" },
      "&:focus-visible": { outline: `2px solid ${rp.outlineCtaBorder}`, outlineOffset: "2px" },
    }}
  >
    <Box
      component="span"
      sx={{ fontSize: "13px", fontWeight: 600, fontFamily: dt.font, lineHeight: "16px", color: rp.outlineCtaText, whiteSpace: "nowrap" }}
    >
      {label}
    </Box>
  </Box>
);

// ---------------------------------------------------------------------
//  Muted "Official OEM package" section (non-OEM states)
// ---------------------------------------------------------------------

export const OemPackageMutedLine: React.FC<{ heading: string; body: string }> = ({ heading, body }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      m: "16px 24px 0 24px",
      pt: "16px",
      borderTop: `1px solid ${rp.rowBorder}`,
    }}
  >
    <Box sx={{ fontSize: "14px", fontWeight: 600, fontFamily: dt.font, lineHeight: "18px", color: rp.oemMutedHeading }}>
      {heading}
    </Box>
    <Box sx={{ fontSize: "12px", fontFamily: dt.font, lineHeight: "17px", color: rp.oemMutedBody }}>
      {body}
    </Box>
  </Box>
);

// Canonical doc chip labels are supplied by the popup (translated); this map
// keeps the type→translation-key wiring in one place for the popup.
export const DOC_TYPE_KEYS: Record<PopupDocType, string> = {
  manual: "docTypeManual",
  electrical: "docTypeElectrical",
  alarms: "docTypeAlarms",
  specSheet: "docTypeSpecSheet",
  // DR-6 — fifth (opt-in) doc type; its lane only ever renders when lit.
  oemNodeDocs: "docTypeOemNodeDocs",
  // READINESS-COMPONENT-DOCS-1 — sixth (opt-in) doc type; lane only renders when lit.
  componentDocs: "docTypeComponentDocs",
};
